import type { FC, ReactNode } from 'react'
import type { Testimonial } from '@/types'

import { Quote } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TestimonialCardProps {
    testimonial: Testimonial
    size?: 'lg' | 'sm'
    className?: string
}

const initialsOf = (name: string): string => {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('')
}

export const TestimonialCard: FC<TestimonialCardProps> = ({ testimonial, size = 'lg', className }): ReactNode => {
    return (
        <figure
            className={cn(
                'flex h-full flex-col rounded-3xl border border-ink/8 bg-white shadow-[0_18px_40px_-28px_rgba(27,18,25,0.35)]',
                size === 'lg' ? 'p-7 sm:p-8' : 'p-6',
                className
            )}
        >
            <Quote className={cn('text-gold', size === 'lg' ? 'size-8' : 'size-6')} aria-hidden='true' />

            <blockquote
                className={cn(
                    'mt-4 flex-1 text-ink',
                    size === 'lg' ? 'font-heading text-2xl leading-snug' : 'text-base leading-relaxed'
                )}
            >
                <p data-mdlfy-cms={`testimonials:${testimonial.id}:quote`}>{testimonial.quote}</p>
            </blockquote>

            <figcaption className='mt-6 flex items-center gap-3'>
                <span aria-hidden='true' className='inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-night text-xs font-bold tracking-wider text-gold'>
                    {initialsOf(testimonial.name)}
                </span>

                <span className='flex flex-col'>
                    <span className='text-sm font-semibold text-ink' data-mdlfy-cms={`testimonials:${testimonial.id}:name`}>{testimonial.name}</span>
                    <span className='text-xs text-muted-foreground'>
                        Guju Events client
                    </span>
                </span>
            </figcaption>
        </figure>
    )
}
